import React, { useState, useEffect } from 'react';
import { Zap, Calendar, Users, Settings } from 'lucide-react';
import FormModal from './FormModal';

interface EmailTrigger {
  id: number;
  name: string;
  type: 'event' | 'schedule' | 'condition' | 'workflow';
  event_type?: string;
  schedule_config?: any;
  condition_config?: any;
  workflow_config?: any;
  is_active: boolean;
  description?: string;
  created_at: string;
  updated_at: string;
}

interface EmailTriggerModalProps {
  onClose: () => void;
  onSubmit: (trigger: Partial<EmailTrigger>) => void;
  selectedTrigger: EmailTrigger | null;
}

export const EmailTriggerModal: React.FC<EmailTriggerModalProps> = ({
  onClose,
  onSubmit,
  selectedTrigger,
}) => {
  const [trigger, setTrigger] = useState<Partial<EmailTrigger>>(
    selectedTrigger || {
      name: '',
      type: 'event',
      is_active: true,
      description: '',
    }
  );

  const [loading, _setLoading] = useState(false);

  // Update state when selectedTrigger changes
  useEffect(() => {
    if (selectedTrigger) {
      setTrigger(selectedTrigger);
    }
  }, [selectedTrigger]);

  const handleSubmit = async () => {
    await onSubmit(trigger);
    onClose();
  };

  const isCreate = !selectedTrigger?.id;

  const triggerTypes = [
    { value: 'event', label: 'Event-Based', description: 'Trigger when specific events occur', icon: Zap },
    { value: 'schedule', label: 'Scheduled', description: 'Send at specific times/dates', icon: Calendar },
    { value: 'condition', label: 'Conditional', description: 'Trigger based on data conditions', icon: Settings },
    { value: 'workflow', label: 'Workflow', description: 'Part of automated sequences', icon: Users },
  ];

  const eventTypes = [
    'user_created',
    'user_registered',
    'user_login',
    'user_logout',
    'password_changed',
    'password_reset_requested',
    'profile_updated',
    'user_profile_updated',
    'role_changed',
    'account_deactivated',
    'user_deleted',
    'user_email_changed_new',
    'user_email_changed_old',
    'user_permissions_granted',
    'user_permissions_revoked',
    'user_permissions_updated',
    'user_permission_profile_assigned',
    'user_permission_profile_removed',
    'user_system_admin_assigned',
    'compliance_score_changed',
    'document_uploaded',
    'document_expired',
    'audit_scheduled',
    'audit_completed',
    'framework_assigned',
    'evidence_submitted',
    'non_conformance_created',
    'task_assigned',
    'task_completed',
    'deadline_approaching',
    'system_alert',
    '2fa_email_code',
    'company_deleted_with_export',
    'custom_event'
  ];

  return (
    <FormModal
      title={isCreate ? 'Create Email Trigger' : 'Edit Email Trigger'}
      subtitle={isCreate ? 'Set up automated email triggers' : 'Modify trigger configuration'}
      icon={<Zap className="text-white" size={24} />}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={isCreate ? 'Create Trigger' : 'Update Trigger'}
      cancelText="Cancel"
      loading={loading}
      size="large"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Trigger Name *</label>
            <input
              type="text"
              value={trigger.name || ''}
              onChange={(e) => setTrigger({ ...trigger, name: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
              placeholder="e.g., Welcome Email Trigger"
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Trigger Type</label>
            <select
              value={trigger.type || 'event'}
              onChange={(e) => setTrigger({ ...trigger, type: e.target.value as any })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
              disabled={loading}
            >
              {triggerTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Description</label>
          <textarea
            value={trigger.description || ''}
            onChange={(e) => setTrigger({ ...trigger, description: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            rows={3}
            placeholder="Describe when and why this trigger fires"
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Active</label>
          <div className="flex items-center mt-3">
            <input
              type="checkbox"
              checked={trigger.is_active || false}
              onChange={(e) => setTrigger({ ...trigger, is_active: e.target.checked })}
              className="h-5 w-5 text-slate-600 rounded focus:ring-2 focus:ring-slate-600 focus:ring-offset-0"
              disabled={loading}
            />
            <span className="ml-3 text-sm text-slate-700">Enable this trigger</span>
          </div>
        </div>

        {/* Event Type Configuration */}
        {trigger.type === 'event' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 border-t border-slate-200 pt-4">Event Configuration</h3>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Event Type</label>
              <select
                value={trigger.event_type || ''}
                onChange={(e) => setTrigger({ ...trigger, event_type: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                disabled={loading}
              >
                <option value="">Select event type</option>
                {eventTypes.map(event => (
                  <option key={event} value={event}>
                    {event.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Schedule Configuration */}
        {trigger.type === 'schedule' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 border-t border-slate-200 pt-4">Schedule Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Frequency</label>
                <select
                  value={trigger.schedule_config?.frequency || 'daily'}
                  onChange={(e) => setTrigger({
                    ...trigger,
                    schedule_config: { ...trigger.schedule_config, frequency: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                  disabled={loading}
                >
                  <option value="once">Once</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Time</label>
                <input
                  type="time"
                  value={trigger.schedule_config?.time || '09:00'}
                  onChange={(e) => setTrigger({
                    ...trigger,
                    schedule_config: { ...trigger.schedule_config, time: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Timezone</label>
                <select
                  value={trigger.schedule_config?.timezone || 'UTC'}
                  onChange={(e) => setTrigger({
                    ...trigger,
                    schedule_config: { ...trigger.schedule_config, timezone: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                  disabled={loading}
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                  <option value="Europe/London">London</option>
                  <option value="Europe/Paris">Paris</option>
                  <option value="Asia/Tokyo">Tokyo</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Condition Configuration */}
        {trigger.type === 'condition' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 border-t border-slate-200 pt-4">Condition Configuration</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Field</label>
                  <select
                    value={trigger.condition_config?.field || ''}
                    onChange={(e) => setTrigger({
                      ...trigger,
                      condition_config: { ...trigger.condition_config, field: e.target.value }
                    })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                    disabled={loading}
                  >
                    <option value="">Select field</option>
                    <option value="compliance_score">Compliance Score</option>
                    <option value="user_role">User Role</option>
                    <option value="days_until_expiry">Days Until Expiry</option>
                    <option value="task_status">Task Status</option>
                    <option value="audit_status">Audit Status</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Operator</label>
                  <select
                    value={trigger.condition_config?.operator || ''}
                    onChange={(e) => setTrigger({
                      ...trigger,
                      condition_config: { ...trigger.condition_config, operator: e.target.value }
                    })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                    disabled={loading}
                  >
                    <option value="">Select operator</option>
                    <option value="equals">Equals</option>
                    <option value="not_equals">Not Equals</option>
                    <option value="greater_than">Greater Than</option>
                    <option value="less_than">Less Than</option>
                    <option value="contains">Contains</option>
                    <option value="not_contains">Does Not Contain</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Value</label>
                  <input
                    type="text"
                    value={trigger.condition_config?.value || ''}
                    onChange={(e) => setTrigger({
                      ...trigger,
                      condition_config: { ...trigger.condition_config, value: e.target.value }
                    })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                    placeholder="Condition value"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Workflow Configuration */}
        {trigger.type === 'workflow' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 border-t border-slate-200 pt-4">Workflow Configuration</h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 text-sm">
                <strong>Note:</strong> Workflow triggers are part of automated email sequences.
                Configure the workflow steps and timing in the Workflow Builder.
              </p>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Workflow Name</label>
              <input
                type="text"
                value={trigger.workflow_config?.name || ''}
                onChange={(e) => setTrigger({
                  ...trigger,
                  workflow_config: { ...trigger.workflow_config, name: e.target.value }
                })}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                placeholder="e.g., Onboarding Sequence"
                disabled={loading}
              />
            </div>
          </div>
        )}
      </div>
    </FormModal>
  );
};